import { IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material"
import { DeleteOutlined, EditOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"

import { InventoryLotListCell } from "../../../../components"
import { formatInventoryLocation } from "../../../../utils"
import type { TransferFormPallet } from "../../types"
import { formatPalletFormDestination } from "../../utils"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { formatNumber } from "@/utils/number"
import { isNil } from "@/utils/utils"

interface TransferFormProductRowDetailsProps {
  pallets: TransferFormPallet[]
  uomName: string
  readOnly?: boolean
  onPalletOverride: (palletReferenceNumber: number) => void
  onPalletRemove: (palletReferenceNumber: number) => void
}

export const TransferFormProductRowDetails: FC<TransferFormProductRowDetailsProps> = ({
  pallets,
  uomName,
  readOnly = false,
  onPalletOverride,
  onPalletRemove,
}) => {
  if (!pallets.length) {
    return (
      <div className='p-6'>
        <InformationMissingAlert title="Pallets haven't been selected yet. Selected pallet details will appear here." />
      </div>
    )
  }

  return (
    <div className='p-6'>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Reference #</TableCell>
              <TableCell>Current Location</TableCell>
              <TableCell>Lot</TableCell>
              <TableCell>Batch</TableCell>
              <TableCell>Qty to Move</TableCell>
              <TableCell>New Location</TableCell>
              <TableCell className='w-20' />
            </TableRow>
          </TableHead>
          <TableBody>
            {pallets.map((pallet) => (
              <TableRow
                key={pallet.referenceNbr}
                className='align-top'
                data-testid={`transfer.form-product-expanded-content.row-${pallet.referenceNbr}`}
              >
                <TableCell>{pallet.referenceNbr}</TableCell>
                <TableCell>{formatInventoryLocation(pallet.location)}</TableCell>
                <TableCell>
                  <InventoryLotListCell
                    lots={pallet.lots}
                    getKey={(lot) => lot.rawInventoryId}
                    render={(lot) => lot.lotNbr ?? ""}
                  />
                </TableCell>
                <TableCell>
                  <InventoryLotListCell
                    lots={pallet.lots}
                    getKey={(lot) => lot.rawInventoryId}
                    render={(lot) => lot.batchNbr ?? ""}
                  />
                </TableCell>
                <TableCell>
                  <InventoryLotListCell
                    lots={pallet.lots}
                    getKey={(lot) => lot.rawInventoryId}
                    render={(lot) => (isNil(lot.totalUnits) ? "" : `${formatNumber(lot.totalUnits)} ${uomName}`)}
                  />
                </TableCell>
                <TableCell>
                  <div className='my-[-8px] flex items-center gap-x-2'>
                    {formatPalletFormDestination(pallet.destination)}
                    <IconButton
                      size='small'
                      disabled={readOnly}
                      onClick={() => onPalletOverride(pallet.referenceNbr)}
                      data-testid={`transfer.form-product-expanded-content.override-location-button-${pallet.referenceNbr}`}
                    >
                      <EditOutlined />
                    </IconButton>
                  </div>
                </TableCell>
                <TableCell>
                  <div className='my-[-8px] flex items-center justify-end'>
                    <IconButton
                      size='small'
                      disabled={readOnly}
                      onClick={() => onPalletRemove(pallet.referenceNbr)}
                      data-testid={`transfer.form-product-expanded-content.remove-button-${pallet.referenceNbr}`}
                    >
                      <DeleteOutlined />
                    </IconButton>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}
